import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "gap-4 md:grid md:grid-cols-2" }
const _hoisted_4 = { class: "flex" }
const _hoisted_5 = { class: "mb-2 flex-1 text-lg font-semibold text-gray-800" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "gap-4 md:grid md:grid-cols-2" }
const _hoisted_8 = { class: "flex" }
const _hoisted_9 = { class: "mb-2 flex-1 text-lg font-semibold text-gray-800" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 1,
  class: "max-w-md gap-4 rounded-sm bg-red-100 px-4 py-3 text-sm text-red-700 shadow"
}
const _hoisted_12 = { class: "gap-4 md:grid md:grid-cols-2" }
const _hoisted_13 = { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }
const _hoisted_14 = {
  for: "ownerUser",
  class: "mb-2 block text-sm font-medium text-gray-800"
}

import { ref, computed, reactive, onMounted, Ref, ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useAuthStore, useUiStore } from '@/stores';
import { useVuelidateWithLogging } from '@/composables/useVuelidateWithLogging';
import { goBack } from '@/composables/useNavigation';
import { ContactService, SelectOptionsService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { required, maxLength } from '@/utils/validators';

import {
    Permission,
    FormAction,
    ContactModel,
    ContactFormState,
    SelectOptionsServiceGetUsersResult,
} from '@/types';

import { ContactEmailForm, ContactPhoneForm } from '@/views/pages/contact/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const props = __props;

const route = useRoute();
const uiStore = useUiStore();
const authenticatedUserId = useAuthStore().user?.id;

const emailRefs = ref<Record<string, InstanceType<typeof ContactEmailForm> | null>>({});
const phoneRefs = ref<Record<string, InstanceType<typeof ContactPhoneForm> | null>>({});
const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const formState = reactive<ContactFormState>({
    name: null,
    emails: [],
    phones: [
        {
            number: null,
            _key: crypto.randomUUID(),
            isMain: true,
        },
    ],
    contactCompany: null,
    ownerUserId: authenticatedUserId ?? null,
});

const rules = computed(() => ({
    name: { required, maxLength: maxLength(255) },
    emails: {},
    phones: { required },
    contactCompany: {},
    ownerUserId: { required },
}));

const { v$, $validateWithLogging } = useVuelidateWithLogging(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                is_active: true,
            },
        });

        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchContact = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ContactService.get<ContactModel>(id);
        formState.name = data.name;
        formState.emails = data.emails.map((email) => ({ ...email, _key: crypto.randomUUID() }));
        formState.phones = data.phones.map((phone) => ({ ...phone, _key: crypto.randomUUID() }));
        formState.contactCompany = data.contactCompany;
        formState.ownerUserId = data.ownerUserId;
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const setEmailRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        emailRefs.value[key] = el as InstanceType<typeof ContactEmailForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete emailRefs.value[key];
    }
};

const setPhoneRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        phoneRefs.value[key] = el as InstanceType<typeof ContactPhoneForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete phoneRefs.value[key];
    }
};

const addEmail = () => {
    formState.emails.push({
        email: null,
        _key: crypto.randomUUID(),
        isMain: formState.emails.length ? false : true,
    });
};

const addPhone = () => {
    formState.phones.push({ number: null, _key: crypto.randomUUID(), isMain: false });
};

const removeEmail = (index: number) => {
    const [removedEmail] = formState.emails.splice(index, 1);
    if (removedEmail.isMain && formState.emails.length > 0) formState.emails[0].isMain = true;
};

const removePhone = (index: number) => {
    if (formState.phones.length <= 1) return;

    const [removedPhone] = formState.phones.splice(index, 1);

    if (removedPhone.isMain) formState.phones[0].isMain = true;
};

const setMainPhone = (key: string) => {
    formState.phones.forEach((phone) => {
        phone.isMain = phone._key === key;
    });
};

const setMainEmail = (key: string) => {
    formState.emails.forEach((email) => {
        email.isMain = email._key === key;
    });
};

const onSubmit = async () => {
    const emailValidationResults = await Promise.all(
        Object.values(emailRefs.value).map((ref) => !ref || ref?.validate?.()),
    );

    const phoneValidationResults = await Promise.all(
        Object.values(phoneRefs.value).map((ref) => ref?.validate?.() ?? false),
    );

    const allValid = [...emailValidationResults, ...phoneValidationResults].every(Boolean);
    const isFormValid = await $validateWithLogging();

    if (!isFormValid || !allValid) return;

    await setLoading(true);

    const payload = {
        ...formState,
        emails: formState.emails.map(({ _key, ...email }) => email),
        phones: formState.phones.map(({ _key, ...phone }) => phone),
    };

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewContactById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ContactService.update(id, payload);
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ContactService.create(payload);
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const contactId = Number(route.params.id);
        if (!isNaN(contactId)) fetchContact(contactId);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('contact.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListContact' } }),
          (
                            (props.action == _unref(FormAction).CREATE &&
                                _ctx.$can(_unref(Permission).CREATE_CONTACT)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_CONTACT))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).name.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
            name: "name",
            type: "text",
            label: _ctx.$t('contact.labels.name'),
            errors: _unref(v$).name.$errors,
            onBlur: _unref(v$).name.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t('contact.form.section.emails.title')), 1),
          _createVNode(_component_UiButton, {
            variant: "primary",
            "text-variant": "white",
            onClick: addEmail
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('contact.form.actions.addEmail')), 1)
            ]),
            _: 1
          })
        ]),
        (formState.emails.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('contact.errors.noEmails')), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_7, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.emails, (item, index) => {
            return (_openBlock(), _createBlock(_unref(ContactEmailForm), {
              key: item._key,
              index: index,
              modelValue: formState.emails[index],
              "onUpdate:modelValue": ($event: any) => ((formState.emails[index]) = $event),
              ref_for: true,
              ref: (el) => setEmailRef(el, item._key),
              onRemove: () => removeEmail(index),
              onSetMain: () => setMainEmail(item._key)
            }, null, 8, ["index", "modelValue", "onUpdate:modelValue", "onRemove", "onSetMain"]))
          }), 128))
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("h2", _hoisted_9, _toDisplayString(_ctx.$t('contact.form.section.phones.title')), 1),
          _createVNode(_component_UiButton, {
            variant: "primary",
            "text-variant": "white",
            onClick: addPhone
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('contact.form.actions.addPhone')), 1)
            ]),
            _: 1
          })
        ]),
        (formState.phones.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('contact.errors.noPhones')), 1)
            ]))
          : _createCommentVNode("", true),
        (_unref(v$).phones.$error)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(v$).phones.$errors[0].$message), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_12, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.phones, (item, index) => {
            return (_openBlock(), _createBlock(_unref(ContactPhoneForm), {
              key: item._key,
              index: index,
              phones: formState.phones,
              modelValue: formState.phones[index],
              "onUpdate:modelValue": ($event: any) => ((formState.phones[index]) = $event),
              ref_for: true,
              ref: (el) => setPhoneRef(el, item._key),
              onRemove: () => removePhone(index),
              onSetMain: () => setMainPhone(item._key),
              "can-remove": formState.phones.length > 1
            }, null, 8, ["index", "phones", "modelValue", "onUpdate:modelValue", "onRemove", "onSetMain", "can-remove"]))
          }), 128))
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_component_UiTextInput, {
            name: "contactCompany",
            type: "text",
            modelValue: _unref(v$).contactCompany.$model,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).contactCompany.$model) = $event)),
            label: _ctx.$t('contact.form.labels.company')
          }, null, 8, ["modelValue", "label"]),
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_14, [
              _createTextVNode(_toDisplayString(_ctx.$t('contact.form.labels.ownerUser')) + " ", 1),
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
            ]),
            _createVNode(_component_UiMultiselect, {
              name: "ownerUser",
              id: "ownerUser",
              modelValue: formState.ownerUserId,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.ownerUserId) = $event)),
              options: usersOptions.value,
              required: _unref(v$).ownerUserId.required !== undefined,
              errors: _unref(v$).ownerUserId?.$errors,
              disabled: !_ctx.$can(_unref(Permission).EDIT_CONTACT_OWNER),
              primitive: "",
              label: "name",
              "value-prop": "id"
            }, null, 8, ["modelValue", "options", "required", "errors", "disabled"])
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})